"use client";

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import {
  ArrowLeftRight,
  Calendar,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  HardDrive,
  Cpu,
  Upload,
  Trash2,
  X,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";

import api from "../http";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
} from "../components/ui/chart";
import SettingsDropdown from "../components/assistant/SettingsDropdown";
import ProfileImageModal from "../components/assistant/ProfileImageModal";
import { useColor } from "@/contexts/ColorContext";
import { appConfig } from "@/appConfig";

type DailyRow = {
  date: string;
  llm_usd: number;
  llm_chf: number | null;
  infra_compute_chf: number;
  infra_database_chf: number;
  infra_storage_chf: number;
  infra_total_chf: number;
  total_chf: number;
};

type LLMCategory = {
  line_item: string;
  amount_usd: number;
  amount_chf: number | null;
};

type InfraCategory = {
  category: string;
  amount_chf: number;
};

type CostsResponse = {
  range: { start: string; end: string };
  exchange_rate: {
    usd_to_chf: number;
    usd_to_chf_latest?: number;
    usd_to_chf_monthly_avg?: Record<string, number>;
  };
  summary: {
    llm_cost_usd: number;
    llm_cost_chf: number;
    infra_cost_chf: number;
    total_cost_chf: number;
  };
  daily: DailyRow[];
  llm_by_category: LLMCategory[];
  infra_by_category: InfraCategory[];
  openai_configured: boolean;
  fx_method?: string;
  fx_finalized_through_month?: string | null;
};

const formatCHF = (v: number | null | undefined) =>
  v == null
    ? "—"
    : v.toLocaleString("de-CH", { style: "currency", currency: "CHF", minimumFractionDigits: 2 });

const formatUSD = (v: number) =>
  v.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

const pad2 = (n: number) => String(n).padStart(2, "0");

// IMPORTANT: use local date math/formatting (avoid Date(iso) + toISOString time zone shifts)
const formatDateInput = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const parseDateInput = (ymd: string) => {
  const [y, m, d] = ymd.split("-").map((v) => Number(v));
  return new Date(y, (m || 1) - 1, d || 1);
};

const formatMonthKeyLabel = (ym: string) => {
  const [y, m] = ym.split("-").map((v) => Number(v));
  const d = new Date(y, (m || 1) - 1, 1);
  return d.toLocaleDateString("de-CH", { month: "short", year: "numeric" });
};

const formatDateLabel = (iso: string) => {
  const d = parseDateInput(iso);
  return d.toLocaleDateString("de-CH", { day: "2-digit", month: "short" });
};

const DATE_RANGE_STORAGE_KEY = "costs.dateRange.v1";

const getDefaultRange = () => {
  const now = new Date();
  return {
    startDate: formatDateInput(new Date(now.getFullYear(), now.getMonth(), 1)),
    endDate: formatDateInput(now),
  };
};

const getInitialDateRange = () => {
  const fallback = getDefaultRange();
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.sessionStorage.getItem(DATE_RANGE_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as { startDate?: unknown; endDate?: unknown };
    if (typeof parsed.startDate !== "string" || typeof parsed.endDate !== "string") {
      return fallback;
    }
    if (parsed.startDate > parsed.endDate) return fallback;
    return { startDate: parsed.startDate, endDate: parsed.endDate };
  } catch {
    return fallback;
  }
};

const CATEGORY_LABELS: Record<string, string> = {
  compute: "Compute",
  database: "Database",
  storage: "Storage",
};

const Costs = () => {
  const { getAccessToken } = useAuth();
  const { themeColor } = useColor();
  const APP_COLOR = appConfig.features.isDemoMode
    ? themeColor
    : appConfig.themeColor;

  const [currentUser, setCurrentUser] = useState<{
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  } | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // Date range — default to current month
  const [startDate, setStartDate] = useState<string>(
    () => getInitialDateRange().startDate,
  );
  const [endDate, setEndDate] = useState<string>(
    () => getInitialDateRange().endDate,
  );

  const [data, setData] = useState<CostsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // CSV import modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [importCsvText, setImportCsvText] = useState("");
  const [importPreview, setImportPreview] = useState<
    Array<{ cost_date: string; category: string; amount_chf: number; source_ref: string; notes?: string }>
  >([]);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{
    inserted: number;
    skipped: number;
    errors: string[];
    total: number;
  } | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const token = await getAccessToken();
        const userData = await api.getCurrentUser(token);
        setCurrentUser(userData);
      } catch {}
    };
    loadUser();
  }, [getAccessToken]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        DATE_RANGE_STORAGE_KEY,
        JSON.stringify({ startDate, endDate }),
      );
    } catch {}
  }, [startDate, endDate]);

  const loadCosts = async () => {
    if (!data) setIsLoading(true);
    setError(null);
    try {
      const token = await getAccessToken();
      const result = await api.getCostsOverview(token, {
        startDate,
        endDate,
      });
      setData(result);
    } catch {
      setError("Kostendaten konnten nicht geladen werden.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (startDate > endDate) {
      setError("Startdatum muss vor dem Enddatum liegen.");
      return;
    }
    loadCosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // Month navigation helpers
  const goToPrevMonth = () => {
    const current = parseDateInput(startDate);
    const monthStart = new Date(current.getFullYear(), current.getMonth(), 1);
    monthStart.setMonth(monthStart.getMonth() - 1);

    const start = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1);
    const end = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
    setStartDate(formatDateInput(start));
    setEndDate(formatDateInput(end));
  };

  const goToNextMonth = () => {
    const today = new Date();
    const current = parseDateInput(startDate);
    const monthStart = new Date(current.getFullYear(), current.getMonth(), 1);
    monthStart.setMonth(monthStart.getMonth() + 1);

    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    if (monthStart > currentMonthStart) return;

    const start = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1);
    const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
    const end =
      monthStart.getFullYear() === today.getFullYear() &&
      monthStart.getMonth() === today.getMonth()
        ? today
        : monthEnd;

    setStartDate(formatDateInput(start));
    setEndDate(formatDateInput(end));
  };

  const goToCurrentMonth = () => {
    const { startDate: start, endDate: end } = getDefaultRange();
    setStartDate(start);
    setEndDate(end);
  };

  const currentMonthLabel = useMemo(() => {
    const d = parseDateInput(startDate);
    return d.toLocaleDateString("de-CH", { month: "long", year: "numeric" });
  }, [startDate]);

  const selectedMonthKey = useMemo(() => startDate.slice(0, 7), [startDate]);
  const finalizedThroughMonth = data?.fx_finalized_through_month ?? null;
  const isSelectedMonthClosed = useMemo(() => {
    if (!finalizedThroughMonth) return false;
    return selectedMonthKey <= finalizedThroughMonth;
  }, [selectedMonthKey, finalizedThroughMonth]);

  // Chart data
  const chartData = useMemo(
    () =>
      data?.daily.map((row) => ({
        ...row,
        label: formatDateLabel(row.date),
      })) ?? [],
    [data],
  );

  const summary = data?.summary;
  const exchangeRateLatest = data?.exchange_rate?.usd_to_chf_latest ?? data?.exchange_rate?.usd_to_chf;
  const exchangeRateMonthlyAvgMap = data?.exchange_rate?.usd_to_chf_monthly_avg;
  const exchangeRateFinalMonth = data?.fx_finalized_through_month ?? null;

  const exchangeRateDisplay = useMemo(() => {
    if (data?.fx_method === "monthly_average" && exchangeRateFinalMonth && exchangeRateMonthlyAvgMap) {
      const v = exchangeRateMonthlyAvgMap[exchangeRateFinalMonth];
      if (typeof v === "number") {
        return { value: v, label: `Monatsdurchschnitt (${formatMonthKeyLabel(exchangeRateFinalMonth)})` };
      }
    }
    if (typeof exchangeRateLatest === "number") {
      return { value: exchangeRateLatest, label: "Aktueller Kurs (Referenz)" };
    }
    return { value: null as number | null, label: "USD → CHF" };
  }, [data?.fx_method, exchangeRateFinalMonth, exchangeRateMonthlyAvgMap, exchangeRateLatest]);

  const parseCsv = (text: string): string[][] => {
    const rows: string[][] = [];
    let current = "";
    let inQuotes = false;
    let row: string[] = [];
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (inQuotes) {
        if (ch === '"' && text[i + 1] === '"') {
          current += '"';
          i++;
        } else if (ch === '"') {
          inQuotes = false;
        } else {
          current += ch;
        }
      } else if (ch === '"') {
        inQuotes = true;
      } else if (ch === "," || ch === ";") {
        row.push(current.trim());
        current = "";
      } else if (ch === "\n" || ch === "\r") {
        if (ch === "\r" && text[i + 1] === "\n") i++;
        row.push(current.trim());
        current = "";
        if (row.some((c) => c.length > 0)) rows.push(row);
        row = [];
      } else {
        current += ch;
      }
    }
    row.push(current.trim());
    if (row.some((c) => c.length > 0)) rows.push(row);
    return rows;
  };

  const buildImportPreview = (text: string) => {
    setImportError(null);
    setImportResult(null);
    if (!text.trim()) {
      setImportPreview([]);
      return;
    }
    const rows = parseCsv(text);
    if (rows.length < 2) {
      setImportPreview([]);
      setImportError("CSV muss mindestens eine Kopfzeile und eine Datenzeile enthalten.");
      return;
    }
    const header = rows[0].map((h) => h.toLowerCase().replace(/\s+/g, "_"));
    const idx = (name: string) => header.indexOf(name);
    const required = ["cost_date", "category", "amount_chf", "source_ref"];
    const missing = required.filter((k) => idx(k) === -1);
    if (missing.length) {
      setImportPreview([]);
      setImportError(
        `Fehlende Spalten: ${missing.join(", ")}. Benötigt: cost_date, category, amount_chf, source_ref (optional: notes)`,
      );
      return;
    }

    const items: Array<{
      cost_date: string;
      category: string;
      amount_chf: number;
      source_ref: string;
      notes?: string;
    }> = [];

    for (let i = 1; i < rows.length; i++) {
      const r = rows[i];
      const costDate = r[idx("cost_date")] ?? "";
      const category = r[idx("category")] ?? "";
      const rawAmount = (r[idx("amount_chf")] ?? "")
        .replace(/'/g, "")
        .replace(/\s/g, "")
        .replace(",", ".");
      const amount = parseFloat(rawAmount);
      const sourceRef = r[idx("source_ref")] ?? "";
      const notes = idx("notes") >= 0 ? r[idx("notes")] : undefined;

      if (!costDate || !category || !sourceRef || isNaN(amount) || amount <= 0) continue;

      items.push({
        cost_date: costDate,
        category,
        amount_chf: amount,
        source_ref: sourceRef,
        notes: notes || undefined,
      });
    }

    setImportPreview(items.slice(0, 500));
    if (!items.length) {
      setImportError("Keine gültigen Zeilen gefunden. Prüfe die Spalten und Werte.");
    }
  };

  const handleImportSubmit = async () => {
    setImportError(null);
    setImportResult(null);
    if (!importPreview.length) {
      setImportError("Nichts zu importieren. Bitte zuerst eine gültige CSV hochladen.");
      return;
    }
    setIsImporting(true);
    try {
      const token = await getAccessToken();
      const result = await api.bulkImportInfraCosts(token, importPreview);
      setImportResult(result);
      if (result.inserted > 0) {
        await loadCosts();
      }
    } catch {
      setImportError("Import fehlgeschlagen.");
    } finally {
      setIsImporting(false);
    }
  };

  const handleDeleteInfraCategory = async (category: string) => {
    const d = new Date(startDate);
    try {
      const token = await getAccessToken();
      await api.deleteMonthlyInfraCost(token, d.getFullYear(), d.getMonth() + 1, category);
      await loadCosts();
    } catch {
      setError("Löschen fehlgeschlagen.");
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-transparent admindiv">
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Desktop header */}
        <header className="hidden lg:flex items-center justify-between px-8 py-4 border-b border-gray-200">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Kosten</h1>
            <p className="text-sm text-slate-600">
              LLM- und Infrastrukturkosten auf einen Blick.
            </p>
          </div>
          <SettingsDropdown
            onOpenProfileModal={() => setIsProfileModalOpen(true)}
            currentUser={currentUser}
          />
        </header>

        {/* Content */}
        <div className="flex-1 p-4 lg:p-8 overflow-auto pb-20 lg:pb-8">
          {/* Mobile header */}
          <div className="flex lg:hidden items-center justify-between mb-4">
            <div>
              <h1 className="text-lg font-semibold text-slate-900">Kosten</h1>
              <p className="text-xs text-slate-600">Kostenübersicht</p>
            </div>
            <SettingsDropdown
              onOpenProfileModal={() => setIsProfileModalOpen(true)}
              currentUser={currentUser}
            />
          </div>

          {/* Date navigation */}
          <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              {/* Month arrows */}
              <div className="flex items-center gap-1 rounded-lg border bg-white px-2 py-2">
                <button
                  onClick={goToPrevMonth}
                  className="p-1 rounded hover:bg-gray-100"
                  aria-label="Vorheriger Monat"
                >
                  <ChevronLeft size={18} className="text-slate-600" />
                </button>
                <button
                  onClick={goToCurrentMonth}
                  className="px-3 py-1 text-sm font-medium text-slate-700 hover:bg-gray-100 rounded"
                >
                  {currentMonthLabel}
                </button>
                {data?.fx_method === "monthly_average" && (
                  <span
                    className={[
                      "ml-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
                      isSelectedMonthClosed
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-amber-200 bg-amber-50 text-amber-700",
                    ].join(" ")}
                  >
                    {isSelectedMonthClosed ? "Abgeschlossen" : "Offen (geschätzt)"}
                  </span>
                )}
                <button
                  onClick={goToNextMonth}
                  className="p-1 rounded hover:bg-gray-100"
                  aria-label="Nächster Monat"
                >
                  <ChevronRight size={18} className="text-slate-600" />
                </button>
              </div>

              {/* Custom date range */}
              <div className="flex items-center gap-2 rounded-lg border bg-white px-3 py-2">
                <Calendar size={16} className="text-slate-500" />
                <div className="flex items-center gap-2 text-sm">
                  <label className="text-slate-600" htmlFor="cost-start">Von</label>
                  <input
                    id="cost-start"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="rounded-md border border-slate-200 px-2 py-1 text-sm focus:border-slate-400 focus:outline-none"
                  />
                </div>
                <span className="text-slate-400">/</span>
                <div className="flex items-center gap-2 text-sm">
                  <label className="text-slate-600" htmlFor="cost-end">Bis</label>
                  <input
                    id="cost-end"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="rounded-md border border-slate-200 px-2 py-1 text-sm focus:border-slate-400 focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* CSV import button */}
            <button
              onClick={() => {
                setImportCsvText("");
                setImportPreview([]);
                setImportError(null);
                setImportResult(null);
                setShowImportModal(true);
              }}
              className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors hover:opacity-90"
              style={{ backgroundColor: APP_COLOR }}
            >
              <Upload size={16} />
              Infra-Kosten importieren
            </button>
          </div>

          {/* OpenAI not configured banner */}
          {data && !data.openai_configured && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              OpenAI Admin API Key ist nicht konfiguriert. LLM-Kosten werden nicht angezeigt.
              Bitte <code className="bg-amber-100 px-1 rounded">OPENAI_ADMIN_API_KEY</code> in der <code className="bg-amber-100 px-1 rounded">.env</code> Datei setzen.
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Summary cards */}
          <div className="grid gap-4 lg:grid-cols-4">
            <Card className="shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Gesamtkosten</CardTitle>
                <div
                  className="rounded-full p-2 text-white"
                  style={{ backgroundColor: APP_COLOR }}
                >
                  <CircleDollarSign size={18} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold">
                  {summary ? formatCHF(summary.total_cost_chf) : "…"}
                </div>
                <p className="text-xs text-slate-500">LLM + Infrastruktur (CHF)</p>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">LLM-Kosten</CardTitle>
                <div className="rounded-full bg-violet-50 p-2 text-violet-600">
                  <Cpu size={18} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold">
                  {summary ? formatCHF(summary.llm_cost_chf) : "…"}
                </div>
                <p className="text-xs text-slate-500">
                  {summary ? formatUSD(summary.llm_cost_usd) : ""} (OpenAI)
                </p>
                {data?.fx_method === "monthly_average" && (
                  <p className="mt-1 text-[11px] text-slate-400">
                    CHF nur für abgeschlossene Monate (Monatsdurchschnitt).
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Infrastruktur</CardTitle>
                <div className="rounded-full bg-emerald-50 p-2 text-emerald-600">
                  <HardDrive size={18} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold">
                  {summary ? formatCHF(summary.infra_cost_chf) : "…"}
                </div>
                <p className="text-xs text-slate-500">Compute, DB, Storage</p>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Wechselkurs</CardTitle>
                <div className="rounded-full bg-sky-50 p-2 text-sky-600">
                  <ArrowLeftRight size={18} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold">
                  {exchangeRateDisplay.value != null ? exchangeRateDisplay.value.toFixed(4) : "—"}
                </div>
                <p className="text-xs text-slate-500">{exchangeRateDisplay.label}</p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            {/* Daily costs chart */}
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Tägliche Kosten</CardTitle>
                <CardDescription>LLM- vs. Infrastrukturkosten pro Tag (CHF)</CardDescription>
              </CardHeader>
              <CardContent>
                {!data && isLoading ? (
                  <div className="py-12 text-center text-sm text-slate-500">Lade Diagramm…</div>
                ) : chartData.length ? (
                  <ChartContainer
                    className="h-[320px] w-full"
                    config={{
                      llm_chf: { label: "LLM (CHF)", color: "#8b5cf6" },
                      infra_total_chf: { label: "Infrastruktur (CHF)", color: "#10b981" },
                    }}
                  >
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <ChartLegend />
                      <Bar dataKey="llm_chf" stackId="a" fill="var(--color-llm_chf)" />
                      <Bar dataKey="infra_total_chf" stackId="a" fill="var(--color-infra_total_chf)" />
                    </BarChart>
                  </ChartContainer>
                ) : (
                  <div className="py-12 text-center text-sm text-slate-500">
                    Keine Daten im ausgewählten Zeitraum.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Cost breakdown */}
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Aufschlüsselung</CardTitle>
                <CardDescription>Kosten nach Kategorie im Zeitraum</CardDescription>
              </CardHeader>
              <CardContent>
                {!data && isLoading ? (
                  <div className="py-12 text-center text-sm text-slate-500">Lade…</div>
                ) : (
                  <div className="space-y-6">
                    {/* LLM categories */}
                    {data?.llm_by_category && data.llm_by_category.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-slate-600 mb-2">LLM (OpenAI)</h4>
                        <div className="space-y-2">
                          {data.llm_by_category.map((cat) => (
                            <div key={cat.line_item} className="flex items-center justify-between text-sm">
                              <span className="text-slate-700">{cat.line_item}</span>
                              <div className="text-right">
                                <span className="font-medium">{formatCHF(cat.amount_chf)}</span>
                                <span className="text-slate-400 ml-2 text-xs">({formatUSD(cat.amount_usd)})</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Infra categories */}
                    {data?.infra_by_category && data.infra_by_category.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-slate-600 mb-2">Infrastruktur</h4>
                        <div className="space-y-2">
                          {data.infra_by_category.map((cat) => (
                            <div key={cat.category} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <span className="text-slate-700">{CATEGORY_LABELS[cat.category] ?? cat.category}</span>
                                <button
                                  onClick={() => handleDeleteInfraCategory(cat.category)}
                                  className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                                  title={`${CATEGORY_LABELS[cat.category] ?? cat.category} löschen`}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                              <span className="font-medium">{formatCHF(cat.amount_chf)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(!data?.llm_by_category?.length && !data?.infra_by_category?.length) && (
                      <div className="py-6 text-center text-sm text-slate-500">
                        Keine Kostendaten vorhanden.
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Daily breakdown table */}
          <div className="mt-6">
            <Card className="shadow-sm">
              <CardHeader>
                <CardTitle>Tägliche Übersicht</CardTitle>
                <CardDescription>Detaillierte Aufschlüsselung pro Tag</CardDescription>
              </CardHeader>
              <CardContent>
                {!data && isLoading ? (
                  <div className="py-8 text-center text-sm text-slate-500">Lade Daten…</div>
                ) : data?.daily && data.daily.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-slate-500">
                          <th className="pb-3 pr-4 font-medium">Datum</th>
                          <th className="pb-3 pr-4 font-medium text-right">LLM (USD)</th>
                          <th className="pb-3 pr-4 font-medium text-right">LLM (CHF)</th>
                          <th className="pb-3 pr-4 font-medium text-right">Compute</th>
                          <th className="pb-3 pr-4 font-medium text-right">Database</th>
                          <th className="pb-3 pr-4 font-medium text-right">Storage</th>
                          <th className="pb-3 font-medium text-right">Total (CHF)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.daily.map((row) => (
                          <tr key={row.date} className="border-t border-slate-100 text-slate-800">
                            <td className="py-2.5 pr-4">{formatDateLabel(row.date)}</td>
                            <td className="py-2.5 pr-4 text-right tabular-nums">{formatUSD(row.llm_usd)}</td>
                            <td className="py-2.5 pr-4 text-right tabular-nums">{formatCHF(row.llm_chf)}</td>
                            <td className="py-2.5 pr-4 text-right tabular-nums">{formatCHF(row.infra_compute_chf)}</td>
                            <td className="py-2.5 pr-4 text-right tabular-nums">{formatCHF(row.infra_database_chf)}</td>
                            <td className="py-2.5 pr-4 text-right tabular-nums">{formatCHF(row.infra_storage_chf)}</td>
                            <td className="py-2.5 text-right font-semibold tabular-nums">{formatCHF(row.total_chf)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-slate-300 font-semibold text-slate-900">
                          <td className="py-3 pr-4">Total</td>
                          <td className="py-3 pr-4 text-right tabular-nums">
                            {summary ? formatUSD(summary.llm_cost_usd) : ""}
                          </td>
                          <td className="py-3 pr-4 text-right tabular-nums">
                            {summary ? formatCHF(summary.llm_cost_chf) : ""}
                          </td>
                          <td className="py-3 pr-4 text-right tabular-nums">
                            {data?.infra_by_category
                              ? formatCHF(
                                  data.infra_by_category.find((c) => c.category === "compute")?.amount_chf ?? 0,
                                )
                              : ""}
                          </td>
                          <td className="py-3 pr-4 text-right tabular-nums">
                            {data?.infra_by_category
                              ? formatCHF(
                                  data.infra_by_category.find((c) => c.category === "database")?.amount_chf ?? 0,
                                )
                              : ""}
                          </td>
                          <td className="py-3 pr-4 text-right tabular-nums">
                            {data?.infra_by_category
                              ? formatCHF(
                                  data.infra_by_category.find((c) => c.category === "storage")?.amount_chf ?? 0,
                                )
                              : ""}
                          </td>
                          <td className="py-3 text-right tabular-nums">
                            {summary ? formatCHF(summary.total_cost_chf) : ""}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="py-8 text-center text-sm text-slate-500">
                    Keine Daten im ausgewählten Zeitraum.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CSV import modal */}
        {showImportModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    Infra-Kosten importieren
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    CSV-Import (täglich). Spalten:{" "}
                    <span className="font-mono text-xs">cost_date, category, amount_chf, source_ref</span>{" "}
                    (optional: <span className="font-mono text-xs">notes</span>)
                  </p>
                </div>
                <button
                  onClick={() => setShowImportModal(false)}
                  className="p-1 rounded hover:bg-gray-100"
                >
                  <X size={20} className="text-slate-500" />
                </button>
              </div>

              <div className="space-y-4">
                {/* File upload */}
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-medium text-slate-900 mb-2">CSV Datei hochladen</div>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="text-sm"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = () => {
                        const text = String(reader.result ?? "");
                        setImportCsvText(text);
                        buildImportPreview(text);
                      };
                      reader.readAsText(file);
                    }}
                  />
                  <div className="mt-2 text-xs text-slate-500">
                    Excel → «Speichern unter → CSV»
                  </div>
                </div>

                {/* Paste area */}
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-medium text-slate-900 mb-2">Oder CSV einfügen</div>
                  <textarea
                    value={importCsvText}
                    onChange={(e) => {
                      setImportCsvText(e.target.value);
                      buildImportPreview(e.target.value);
                    }}
                    className="w-full min-h-[120px] rounded-md border border-slate-200 bg-white text-slate-900 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none font-mono"
                    placeholder={"cost_date,category,amount_chf,source_ref,notes\n2026-02-01,db,12.34,coorpix:2026-02-01:db,Neon daily cost"}
                  />
                </div>

                {/* Error */}
                {importError && (
                  <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    <AlertCircle size={16} className="mt-0.5 shrink-0" />
                    {importError}
                  </div>
                )}

                {/* Import result */}
                {importResult && (
                  <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                    <CheckCircle size={16} className="mt-0.5 shrink-0" />
                    <div>
                      <span className="font-medium">{importResult.inserted}</span> eingefügt,{" "}
                      <span className="font-medium">{importResult.skipped}</span> übersprungen (Duplikate)
                      {importResult.errors.length > 0 && (
                        <div className="mt-1 text-red-700 text-xs">
                          {importResult.errors.map((e, i) => <div key={i}>{e}</div>)}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Preview table */}
                {importPreview.length > 0 && (
                  <div>
                    <div className="text-sm font-medium text-slate-700 mb-2">
                      Vorschau ({importPreview.length} Zeile{importPreview.length !== 1 ? "n" : ""})
                    </div>
                    <div className="max-h-[240px] overflow-auto rounded-md border border-slate-200">
                      <table className="min-w-full text-xs">
                        <thead className="bg-slate-100 sticky top-0">
                          <tr className="text-left text-slate-600">
                            <th className="px-3 py-2 font-medium">Datum</th>
                            <th className="px-3 py-2 font-medium">Kategorie</th>
                            <th className="px-3 py-2 font-medium text-right">Betrag (CHF)</th>
                            <th className="px-3 py-2 font-medium">Source Ref</th>
                            <th className="px-3 py-2 font-medium">Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importPreview.map((item, i) => (
                            <tr key={i} className="border-t border-slate-100">
                              <td className="px-3 py-1.5">{item.cost_date}</td>
                              <td className="px-3 py-1.5">{item.category}</td>
                              <td className="px-3 py-1.5 text-right tabular-nums">{item.amount_chf.toFixed(2)}</td>
                              <td className="px-3 py-1.5 font-mono text-slate-500">{item.source_ref}</td>
                              <td className="px-3 py-1.5 text-slate-500">{item.notes ?? ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-gray-100 transition-colors"
                >
                  Schliessen
                </button>
                <button
                  onClick={handleImportSubmit}
                  disabled={isImporting || importPreview.length === 0}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: APP_COLOR }}
                >
                  {isImporting ? "Importiere…" : `${importPreview.length} Einträge importieren`}
                </button>
              </div>
            </div>
          </div>
        )}

        <ProfileImageModal
          isOpen={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
          currentUser={currentUser}
          onProfileImageUpdate={(imageUrl) => {
            if (currentUser) {
              setCurrentUser({ ...currentUser, profile_image_url: imageUrl });
            }
          }}
          onUserInfoUpdate={(updatedUser) => {
            setCurrentUser(updatedUser);
          }}
        />
      </div>
    </div>
  );
};

export default Costs;
